import { createMapBounds, expandMapBounds, getLongitudeSpan } from './bounds'
import type { FitMapBoundsOptions, MapCoordinate, MapHandle } from './types'

interface FitBoundsWithExtraSpaceOptions {
  expansion?: number
  wrappedExpansion?: number
  minExtraSpace?: number
}

export function getBoundsFromMarkers(markers: Array<{ latitude: number, longitude: number }>) {
  return createMapBounds(markers.map((marker) => ({
    lat: marker.latitude,
    lng: marker.longitude,
  })))
}

export function fitMarkersOnMap(
  map: MapHandle | null,
  markers: Array<{ latitude: number, longitude: number }>,
  padding: FitMapBoundsOptions['padding'] = 0,
) {
  if (!map || markers.length === 0) {
    return
  }

  if (markers.length === 1) {
    map.setViewport({
      longitude: markers[0].longitude,
      latitude: markers[0].latitude,
      zoom: 15,
    })
    return
  }

  const bounds = getBoundsFromMarkers(markers)
  if (!bounds) {
    return
  }

  map.fitBounds(bounds, { padding, durationMs: 0 })
}

export function fitBoundsWithExtraSpace(
  map: MapHandle | null,
  bounds: {
    west: number
    south: number
    east: number
    north: number
  },
  options: FitBoundsWithExtraSpaceOptions = {},
) {
  if (!map) {
    return
  }

  const longitudeSpan = getLongitudeSpan(bounds.east, bounds.west)
  const minExtraSpace = options.minExtraSpace ?? 0.00001
  const expansion = longitudeSpan > 200
    ? (options.wrappedExpansion ?? 0.03)
    : (options.expansion ?? 0.2)
  const latExpansion = Math.max((bounds.north - bounds.south) * expansion, minExtraSpace)
  const lngExpansion = Math.max(longitudeSpan * expansion, minExtraSpace)
  const expandedBounds = expandMapBounds(bounds, latExpansion, lngExpansion)

  map.fitBounds(expandedBounds, { durationMs: 0 })
}

export function panMapWithPixelOffset(
  map: MapHandle | null,
  coordinate: MapCoordinate,
  xOffset: number = 0,
  yOffset: number = 0,
) {
  if (!map) {
    return
  }

  const projected = map.project(coordinate)
  const unprojected = map.unproject({ x: projected.x - xOffset, y: projected.y - yOffset })

  map.setViewport({
    longitude: unprojected.lng,
    latitude: unprojected.lat,
    zoom: map.getZoom(),
  }, { animate: false })
}
